import type { SocketClosed } from '../types'

/**
 * What to put on screen when the socket closes.
 *
 * The agent puts real text in its close reasons and the default is to show it,
 * so a reason added on the server appears without anyone editing this file.
 *
 * Two exceptions, both because the agent's wording is for a log and not for a
 * stranger at a table. The first is the idle timeout, which arrives as a clean
 * 1000 with the bare words "idle timeout". The second is the menu failure,
 * verified live against the agent:
 *
 *   menu fetch failed: Client error '404 Not Found' for url
 *   'http://localhost:8000/restaurant/menu?property_id=99999'\nFor more
 *
 * That is an internal URL, truncated mid-sentence by the server's 123-byte cap,
 * shown to a stranger at a table. It gets a human sentence instead; the detail
 * is in the agent's log, where someone can act on it.
 *
 * Returns null when there is nothing worth saying — a clean close is the normal
 * end of a conversation, not an error.
 */
export function closeMessage({ code, reason }: SocketClosed): string | null {
  if (reason === 'idle timeout') {
    // "New order", not "carry on": the agent keeps the order in memory for the
    // life of the connection, so there is nothing left to carry on with.
    return 'We ended the call after a few quiet minutes. Tap to start a new order.'
  }
  if (reason.startsWith('menu fetch failed')) {
    return "This table's menu isn't available right now. Please ask a member of staff."
  }
  if (reason.trim()) return reason
  if (code === 1000 || code === 1005) return null
  return 'The connection dropped.'
}
